'use client';

import { c as _c } from "react/compiler-runtime";
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useMemo } from 'react';
import { AI_REVIEW_TAB_LABELS, AI_REVIEW_TABS, SETTINGS_TABS } from '@/lib/constants';
import { FIX_SESSIONS_LABEL } from '@/lib/fix-sessions/paths';
import { resolveIntegration } from '@/lib/integrations/settings-pages';
import { buildInternalTitle } from '@/lib/page-titles';
import { INTEGRATION_PAGE_TITLES, SETTINGS_TAB_TITLES } from '@/lib/settings/registry';
const SUPER_ADMIN_TITLES: Record<string, string> = {
  '': 'Dashboard',
  'github-query': 'GitHub API Query',
  'trigger-review': 'Trigger AI review',
  'repo-scan': 'Repository scan',
  'critical-issues': 'Critical issues',
  'installation': 'Installation impersonate',
  'wikis': 'Wiki management',
  'migrations': 'Migrations',
  'review-runs': 'Review runs',
  'violations': 'All violations',
  'competitor-analysis': 'Competitor analysis',
  'user-research': 'User research',
  'users': 'User details'
};
function getTitleForInternalPath(pathname: string, searchParams: URLSearchParams): string | null {
  const segments = pathname.split('/').filter(Boolean);
  const root = segments[0];
  switch (root) {
    case 'dashboard':
      return buildInternalTitle('Dashboard');
    case 'inbox':
      return buildInternalTitle('Pull requests');
    case 'analytics':
      return buildInternalTitle('Analytics');
    case 'error':
      return buildInternalTitle('Error');
    case 'pricing':
      return buildInternalTitle('Pricing');
    case 'agents-library':
      return buildInternalTitle('Agents library');
    case 'refer':
      return buildInternalTitle('Referrals');
    case 'onboarding':
      {
        const step = segments[1] ?? '';
        if (step === 'welcome') {
          return buildInternalTitle('Welcome');
        }
        if (step === 'first-code-review') {
          return buildInternalTitle('First code review');
        }
        if (step === 'ai-reviewer-setup') {
          return buildInternalTitle('AI reviewer setup');
        }
        if (step === 'free-trial') {
          return buildInternalTitle('Free trial');
        }
        return buildInternalTitle('Onboarding');
      }
    case 'ai-review':
      {
        const tab = searchParams.get('tab')?.toLowerCase();
        const tabKey = tab && Object.prototype.hasOwnProperty.call(AI_REVIEW_TAB_LABELS, tab) ? tab as keyof typeof AI_REVIEW_TAB_LABELS : undefined;
        const tabLabel = tabKey ? AI_REVIEW_TAB_LABELS[tabKey] : undefined;
        if (tabKey && tabLabel && tabKey !== AI_REVIEW_TABS.SETUP) {
          return buildInternalTitle('AI review settings', tabLabel);
        }
        return buildInternalTitle('AI review settings');
      }
    case 'settings':
      {
        const rawTab = searchParams.get('tab')?.toLowerCase();
        const tab = Object.values(SETTINGS_TABS).find(value => value === rawTab) ?? SETTINGS_TABS.GITHUB;
        // Both Integrations and API, CLI & MCP open tool pages through `integration`.
        const integration = resolveIntegration(searchParams.get('integration')?.toLowerCase() ?? null);
        return buildInternalTitle('Settings', SETTINGS_TAB_TITLES[tab], integration ? INTEGRATION_PAGE_TITLES[integration] : undefined);
      }
    case 'wiki':
      {
        if (segments[1] === 'search') {
          return buildInternalTitle('Wiki', 'Search');
        }
        if (segments.length >= 3) {
          return buildInternalTitle('Wiki', `${segments[1]}/${segments[2]}`);
        }
        return buildInternalTitle('Wiki');
      }
    case 'codebase-scan':
      {
        if (segments.length > 1) {
          return buildInternalTitle('Codebase scan', 'Details');
        }
        return buildInternalTitle('Codebase scan');
      }
    case 'pr':
      {
        if (segments.length >= 5 && segments[3] === 'pull') {
          const owner = segments[1];
          const repo = segments[2];
          const number = segments[4];
          return buildInternalTitle(`PR #${number} | ${owner}/${repo}`);
        }
        return buildInternalTitle('Pull request');
      }
    case 'code':
      {
        // Always a title under /code, so a fix session's never sticks after moving to code sessions.
        if (segments[1] === 'fix') {
          return buildInternalTitle(FIX_SESSIONS_LABEL, segments.length > 2 ? 'Session' : undefined);
        }
        return buildInternalTitle('Code');
      }
    case 'super-admin':
      {
        const section = segments[1] ?? '';
        const sectionTitle = SUPER_ADMIN_TITLES[section] ?? 'Dashboard';
        const titleSegments = ['Super admin', sectionTitle];
        if (section === 'repo-scan' && segments[2]) {
          titleSegments.push('Details');
        }
        return buildInternalTitle(...titleSegments);
      }
    case 'admin':
      return buildInternalTitle('Super admin');
    case 'action':
      if (segments[1] === 'fix' && segments[2] === 'violation') {
        return buildInternalTitle('Fix violation');
      }
      return null;
    default:
      return null;
  }
}
export function TitleManager() {
  const $ = _c(10);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams.toString();
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const searchKey = t0;
  let t1;
  if ($[2] !== pathname || $[3] !== searchKey) {
    const params = new URLSearchParams(searchKey);
    t1 = getTitleForInternalPath(pathname, params);
    $[2] = pathname;
    $[3] = searchKey;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const title = t1;
  let t2;
  if ($[5] !== title) {
    t2 = () => {
      if (!title) {
        return;
      }
      document.title = title;
      const observer = new MutationObserver(() => {
        if (document.title !== title) {
          document.title = title;
        }
      });
      observer.observe(document.head, {
        childList: true,
        subtree: true,
        characterData: true
      });
      return () => observer.disconnect();
    };
    $[5] = title;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== pathname || $[8] !== title) {
    t3 = [title, pathname];
    $[7] = pathname;
    $[8] = title;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  useEffect(t2, t3);
  return null;
}